import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import {
  Key,
  Plus,
  Edit2,
  Trash2,
  Power,
  PowerOff,
  Check,
  X,
  AlertCircle,
  Zap,
  Eye,
  EyeOff,
} from 'lucide-react';

const AIProviderManagement = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showApiKey, setShowApiKey] = useState({});

  const [formData, setFormData] = useState({
    provider_type: 'openai',
    provider_name: '',
    api_key: '',
    is_enabled: true,
    text_model: '',
    vision_model: '',
    assigned_features: [],
  });

  const providerTypes = [
    { value: 'emergent', label: 'Emergent Universal Key' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'gemini', label: 'Google Gemini' },
    { value: 'claude', label: 'Anthropic Claude' },
  ];

  const featureTypes = [
    { value: 'stl_analysis', label: 'STL Analysis' },
    { value: 'image_analysis', label: 'Image Analysis' },
    { value: 'chat', label: 'AI Chat' },
    { value: 'slicer_recommendations', label: 'Slicer Recommendations' },
  ];

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await api.get('/admin/ai-providers');
      setProviders(response.data);
    } catch (error) {
      showMessage('error', 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/ai-providers/${editing.id}`, formData);
        showMessage('success', 'Provider updated successfully');
      } else {
        await api.post('/admin/ai-providers', formData);
        showMessage('success', 'Provider created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchProviders();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this provider?')) return;
    try {
      await api.delete(`/admin/ai-providers/${id}`);
      showMessage('success', 'Provider deleted successfully');
      fetchProviders();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleEdit = (provider) => {
    setEditing(provider);
    setFormData({
      provider_type: provider.provider_type,
      provider_name: provider.provider_name,
      api_key: '', // Don't load the key for security
      is_enabled: provider.is_enabled,
      text_model: provider.text_model || '',
      vision_model: provider.vision_model || '',
      assigned_features: provider.assigned_features,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      provider_type: 'openai',
      provider_name: '',
      api_key: '',
      is_enabled: true,
      text_model: '',
      vision_model: '',
      assigned_features: [],
    });
  };

  const toggleFeature = (feature) => {
    setFormData((prev) => ({
      ...prev,
      assigned_features: prev.assigned_features.includes(feature)
        ? prev.assigned_features.filter((f) => f !== feature)
        : [...prev.assigned_features, feature],
    }));
  };

  const getProviderIcon = (type) => {
    const icons = {
      emergent: '⚡',
      openai: '🤖',
      gemini: '💎',
      claude: '🧠',
    };
    return icons[type] || '🔧';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            AI Provider Management
          </h1>
          <p className="text-gray-600">
            Configure and manage AI providers for PrintMind AI
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            {message.text}
          </div>
        )}

        {/* Add Provider Button */}
        <div className="mb-6">
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus size={20} />
            Add Provider
          </button>
        </div>

        {/* Providers List */}
        <div className="grid gap-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : providers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No providers configured
            </div>
          ) : (
            providers.map((provider) => (
              <div
                key={provider.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="text-4xl">{getProviderIcon(provider.provider_type)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {provider.provider_name}
                        </h3>
                        {provider.is_default && (
                          <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                            Default
                          </span>
                        )}
                        {provider.is_enabled ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <Power size={14} /> Enabled
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-gray-400 text-sm">
                            <PowerOff size={14} /> Disabled
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-3">
                        Type: <span className="font-medium">{provider.provider_type}</span>
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {provider.assigned_features.map((feature) => (
                          <span
                            key={feature}
                            className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded"
                          >
                            {feature.replace('_', ' ')}
                          </span>
                        ))}
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        {provider.text_model && (
                          <div>
                            <span className="font-medium">Text Model:</span> {provider.text_model}
                          </div>
                        )}
                        {provider.vision_model && (
                          <div>
                            <span className="font-medium">Vision Model:</span> {provider.vision_model}
                          </div>
                        )}
                      </div>

                      <div className="mt-3 text-xs text-gray-500">
                        API Key: {provider.has_api_key ? '✓ Configured' : '✗ Not set'}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(provider)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                      title="Edit"
                    >
                      <Edit2 size={18} />
                    </button>
                    {!provider.is_default && (
                      <button
                        onClick={() => handleDelete(provider.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">
                    {editing ? 'Edit Provider' : 'Add Provider'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Provider Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provider Type
                    </label>
                    <select
                      value={formData.provider_type}
                      onChange={(e) =>
                        setFormData({ ...formData, provider_type: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      disabled={editing?.is_default}
                    >
                      {providerTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Provider Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provider Name
                    </label>
                    <input
                      type="text"
                      value={formData.provider_name}
                      onChange={(e) =>
                        setFormData({ ...formData, provider_name: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., My OpenAI Account"
                      required
                    />
                  </div>

                  {/* API Key */}
                  {formData.provider_type !== 'emergent' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKey[formData.provider_type] ? 'text' : 'password'}
                          value={formData.api_key}
                          onChange={(e) =>
                            setFormData({ ...formData, api_key: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 pr-10"
                          placeholder={editing ? 'Leave blank to keep existing key' : 'Enter API key'}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowApiKey((prev) => ({
                              ...prev,
                              [formData.provider_type]: !prev[formData.provider_type],
                            }))
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        >
                          {showApiKey[formData.provider_type] ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Text Model */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Text Model (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.text_model}
                      onChange={(e) =>
                        setFormData({ ...formData, text_model: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., gpt-4o-mini, claude-3-sonnet"
                    />
                  </div>

                  {/* Vision Model */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vision Model (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.vision_model}
                      onChange={(e) =>
                        setFormData({ ...formData, vision_model: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., gpt-4o-mini, gemini-pro-vision"
                    />
                  </div>

                  {/* Assigned Features */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Features
                    </label>
                    <div className="space-y-2">
                      {featureTypes.map((feature) => (
                        <label key={feature.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.assigned_features.includes(feature.value)}
                            onChange={() => toggleFeature(feature.value)}
                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">{feature.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Enabled Toggle */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="enabled"
                      checked={formData.is_enabled}
                      onChange={(e) =>
                        setFormData({ ...formData, is_enabled: e.target.checked })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
                      Enable this provider
                    </label>
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      {editing ? 'Update Provider' : 'Create Provider'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIProviderManagement;
